import ServiceCollection from "src/services/ServiceCollection";
import {getPermissions} from "src/components/AppPermissions";
import {AppModules} from "src/models/permissions/RolePermissions";
import React, {useEffect, useRef, useState} from "react";
import {Box, Button, SpaceBetween} from "@amzn/awsui-components-react";
import CreateJEWorksheetRequest from "src/models/je-allocation/CreateJEWorksheetRequest";
import CreateJEWorksheetTemplateModal
    from "src/components/jeAllocation/worksheet-template/CreateJEWorksheetTemplateModal";
import CreateJEWorksheetModal from "src/components/jeAllocation/worksheet/CreateJEWorksheetModal";
import TPENavigator from "src/components/TPENavigator";
import TPPolarisTable from "src/components/shared/TPPolarisTable";
import {
    JE_WORKSHEET_DEFAULT_COLLECTION_PREFERENCES,
    JE_WORKSHEET_TEMPLATE_DEFAULT_COLLECTION_PREFERENCES,
    JEWorksheetTableColumns,
    JEWorksheetTemplateTableColumns,
    PAGE_SIZE_OPTIONS,
    WORKSHEET_TEMPLATE_VISIBLE_CONTENT_OPTIONS,
    WORKSHEET_VISIBLE_CONTENT_OPTIONS
} from "src/components/jeAllocation/Constants";
import {
    getJEWorksheetDashboardTableFilteringProperties,
    prepareWorksheetTableColumnDefinitions
} from "src/components/jeAllocation/worksheet/WorksheetTableUtils";
import {
    getJEWorksheetTemplateDashboardTableFilteringProperties,
    prepareWorksheetTemplateTableColumnDefinitions
} from "src/components/jeAllocation/worksheet-template/WorksheetTemplateTableUtils";
import CONSTANTS from "src/utils/constants";

export default function JEAllocationView(props: { services: ServiceCollection }) {

    const {services} = props;
    const {canEdit} = getPermissions(AppModules.JE_ALLOCATION);
    const [navigateURL, setNavigateURL] = React.useState('');
    const [showCreateJEWorksheetModal, setShowCreateJEWorksheetModal] = useState(false);
    const [showCreateJEWorksheetTemplateModal, setShowCreateJEWorksheetTemplateModal] = useState(false);
    const [createJEWorksheetPayload, setCreateJEWorksheetPayload] = useState(null as unknown as CreateJEWorksheetRequest);
    const [newJEWorksheetTemplateName, setNewJEWorksheetTemplateName] = useState(null as unknown as string);
    const [createNewJEWorksheetTemplate, setCreateNewJEWorksheetTemplate] = useState(null as unknown as string);
    const jeWorksheetDashboardColumnDefinitionsRef = useRef<any[]>([]);
    const jeWorksheetTemplateDashboardColumnDefinitionsRef = useRef<any[]>([]);
    const jeWorksheetDashboardFilteringPropertiesRef = useRef<any[]>([]);
    const jeWorksheetTemplateDashboardFilteringPropertiesRef = useRef<any[]>([]);

    const [listJEWorksheetsResult, isListing, listError] = services.jeAllocationService.getJEWorksheetsAndTemplates('get');
    const [coaSegmentsResponse, coaSegmentsLoading, coaSegmentError] = services.jeAllocationService.getAllCoaSegments('get');
    const [createJEWorksheetResponse, isCreatingWorksheet, createWorksheetError] = services.jeAllocationService.createJEWorksheet(createJEWorksheetPayload);
    const [createJEWorksheetTemplateResponse, isCreatingWorksheetTemplate, createWorksheetTemplateError] =
        services.jeAllocationService.createJEWorksheetTemplate(createNewJEWorksheetTemplate, newJEWorksheetTemplateName);

    useEffect(() => {
        jeWorksheetDashboardColumnDefinitionsRef.current = prepareWorksheetTableColumnDefinitions();
        jeWorksheetTemplateDashboardColumnDefinitionsRef.current = prepareWorksheetTemplateTableColumnDefinitions();
        jeWorksheetDashboardFilteringPropertiesRef.current = getJEWorksheetDashboardTableFilteringProperties();
        jeWorksheetTemplateDashboardFilteringPropertiesRef.current = getJEWorksheetTemplateDashboardTableFilteringProperties();
    }, [listJEWorksheetsResult]);

    useEffect(() => {
        if (createJEWorksheetResponse == null) {
            return;
        }
        services.messageService.showSuccessAutoDismissBanner(`JE Allocation worksheet ${createJEWorksheetResponse.worksheetName} successfully created.`)
        if (createJEWorksheetPayload.templateId != null) {
            navigateToLinkedJEWorksheet(createJEWorksheetResponse.worksheetId);
        } else {
            navigateToStandaloneJEWorksheet(createJEWorksheetResponse.worksheetId);
        }
    }, [createJEWorksheetResponse])

    useEffect(() => {
        if (createJEWorksheetTemplateResponse == null) {
            return;
        }
        services.messageService.showSuccessAutoDismissBanner(`JE Allocation worksheet template ${createJEWorksheetTemplateResponse.templateName} successfully created.`)
        navigateToJEWorksheetTemplate(createJEWorksheetTemplateResponse.templateId);
    }, [createJEWorksheetTemplateResponse])

    const navigateToStandaloneJEWorksheet = (worksheetId: string) => {
        setNavigateURL(`${CONSTANTS.PAGE_NAV.JE_ALLOCATION_STANDALONE.URL}/${btoa(worksheetId)}`)
    }

    const navigateToLinkedJEWorksheet = (worksheetId: string) => {
        setNavigateURL(`${CONSTANTS.PAGE_NAV.JE_ALLOCATION_LINKED.URL}/${btoa(worksheetId)}`)
    }

    const navigateToJEWorksheetTemplate = (templateId: string) => {
        setNavigateURL(`${CONSTANTS.PAGE_NAV.JE_ALLOCATION_TEMPLATE.URL}/${btoa(templateId)}`)
    }

    return <React.Fragment>
        <TPENavigator path={navigateURL} />
        <SpaceBetween direction="vertical" size="l">
            { canEdit && <Box float="right" key={"jeAllocationView_actions"}>
                <SpaceBetween direction="horizontal" size="xs">
                    <Button key="jeallocationView_actions_createTemplate" onClick={() => setShowCreateJEWorksheetTemplateModal(true)}>Create new template</Button>
                    <Button variant="primary" key="jeallocationView_actions_createWorksheet" onClick={() => setShowCreateJEWorksheetModal(true)}>Create new worksheet</Button>
                </SpaceBetween>
            </Box> }
            {listJEWorksheetsResult && (
                <>
                    {listJEWorksheetsResult.worksheets && (
                        <TPPolarisTable
                            sortingColumn="worksheetName"
                            tableEmptyState="No worksheets available"
                            headerTitle="Worksheets"
                            columnDefinitions={jeWorksheetDashboardColumnDefinitionsRef.current}
                            tableItems={listJEWorksheetsResult.worksheets}
                            defaultPreferences={JE_WORKSHEET_DEFAULT_COLLECTION_PREFERENCES}
                            preferencesStorageKey="jeStatusDashboardPreferencesStorageKey"
                            visibleContentOptions={WORKSHEET_VISIBLE_CONTENT_OPTIONS}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            filteringEmptyText="There are no worksheets for the selected filter conditions."
                            filteringLoadingText="Loading worksheets for the filter conditions applied."
                            filteringProperties={jeWorksheetDashboardFilteringPropertiesRef.current}
                            loadingText="Loading worksheets."
                            resizableColumns={true}
                            visibleColumns={JEWorksheetTableColumns}
                            isLoading={isListing}
                        />
                    )}

                    {listJEWorksheetsResult.templates && (
                        <TPPolarisTable
                            sortingColumn="templateName"
                            tableEmptyState="No worksheet templates available"
                            headerTitle="Worksheet Templates"
                            columnDefinitions={jeWorksheetTemplateDashboardColumnDefinitionsRef.current}
                            tableItems={listJEWorksheetsResult.templates}
                            defaultPreferences={JE_WORKSHEET_TEMPLATE_DEFAULT_COLLECTION_PREFERENCES}
                            preferencesStorageKey="jeStatusTemplateDashboardPreferencesStorageKey"
                            visibleContentOptions={WORKSHEET_TEMPLATE_VISIBLE_CONTENT_OPTIONS}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            filteringEmptyText="There are no worksheet templates for the selected filter conditions."
                            filteringLoadingText="Loading worksheet templates for the filter conditions applied."
                            filteringProperties={jeWorksheetTemplateDashboardFilteringPropertiesRef.current}
                            loadingText="Loading worksheet templates."
                            resizableColumns={true}
                            visibleColumns={JEWorksheetTemplateTableColumns}
                            isLoading={isListing}
                        />
                    )}
                </>
            )}
            <CreateJEWorksheetModal
                visible={showCreateJEWorksheetModal}
                templates={listJEWorksheetsResult?.templates || []}
                sevenSegmentLOVMap={coaSegmentsResponse || new Map<string, string[]>()}
                isCreating={isCreatingWorksheet}
                createError={createWorksheetError}
                onClose={() => setShowCreateJEWorksheetModal(false)}
                onSubmit={setCreateJEWorksheetPayload}
            />
            <CreateJEWorksheetTemplateModal
                visible={showCreateJEWorksheetTemplateModal}
                isCreating={isCreatingWorksheetTemplate}
                createError={createWorksheetTemplateError}
                onClose={() => setShowCreateJEWorksheetTemplateModal(false)}
                onSubmit={(templateName: string) => {
                    setNewJEWorksheetTemplateName(templateName);
                    setCreateNewJEWorksheetTemplate((new Date()).toString());
                }}
            />
        </SpaceBetween>
    </React.Fragment>
}
